import { useEffect, useState, useCallback } from "react";
import { endpoints, formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { formatCurrency } from "../../lib/romEngine";
import {
    ClockCounterClockwise, Clock, CurrencyDollar, CheckCircle, WarningCircle,
    Trash, ArrowsClockwise, FunnelSimple,
} from "@phosphor-icons/react";

const STATUS_CFG = {
    pending: { label: "Pending", chip: "chip-orange" },
    approved: { label: "Approved", chip: "chip-green" },
    rejected: { label: "Rejected", chip: "chip-gray" },
};

export default function TimeEntries() {
    const { user } = useAuth();
    const isManage = ["admin", "manager"].includes(user?.role);
    const [items, setItems] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [sel, setSel] = useState(new Set());
    const [filters, setFilters] = useState({ employee_id: "", approval_status: "", date_from: "", date_to: "" });

    const load = useCallback(async () => {
        setLoading(true);
        const params = { per_page: 300 };
        Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
        try {
            const [entRes, sumRes] = await Promise.all([endpoints.timeEntries(params), endpoints.timeEntriesSummary()]);
            setItems(entRes.data?.items || []);
            setSummary(sumRes.data);
            setSel(new Set());
        } catch (e) { /* ignore */ }
        finally { setLoading(false); }
    }, [filters]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => { endpoints.employees({ active_only: false }).then((r) => setEmployees(r.data || [])).catch(() => {}); }, []);

    function toggle(id) {
        setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
    }
    function toggleAll() {
        setSel((s) => s.size === items.length ? new Set() : new Set(items.map((i) => i.id)));
    }
    async function bulkDelete() {
        if (!window.confirm(`Delete ${sel.size} selected entr${sel.size === 1 ? "y" : "ies"}?`)) return;
        try { await endpoints.bulkDeleteTime([...sel]); await load(); }
        catch (e) { alert(formatApiError(e)); }
    }

    const setF = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

    return (
        <div className="max-w-[1500px] mx-auto px-6 py-8 space-y-6" data-testid="time-entries-page">
            <header className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.2em] text-[var(--rom-text-3)] font-semibold mb-1">Timesheets</div>
                    <h1 className="font-display font-black text-3xl tracking-tight flex items-center gap-3">
                        <ClockCounterClockwise size={28} weight="bold" className="text-[var(--rom-orange)]" /> Time Entries
                    </h1>
                    <p className="text-sm text-[var(--rom-text-2)] mt-1">Every captured hour across the company, valued at employee cost rates.</p>
                </div>
                <button onClick={load} className="btn btn-ghost"><ArrowsClockwise size={14} className={loading ? "animate-spin" : ""} /> Refresh</button>
            </header>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <Kpi icon={Clock} label="Total hours" value={summary ? `${summary.total_hours}h` : "—"} accent="navy" />
                <Kpi icon={CurrencyDollar} label="Labour cost" value={summary ? formatCurrency(summary.total_cost) : "—"} accent="orange" />
                <Kpi icon={WarningCircle} label="Pending approval" value={summary ? summary.pending_approval : "—"} accent="orange" />
                <Kpi icon={CheckCircle} label="Allocated to jobs" value={summary ? summary.allocated : "—"} accent="green" />
            </div>

            <div className="rom-card">
                <div className="rom-card-head flex-wrap gap-3">
                    <div className="flex items-center gap-2 flex-wrap">
                        <FunnelSimple size={15} className="text-[var(--rom-text-3)]" />
                        <select data-testid="filter-employee" value={filters.employee_id} onChange={(e) => setF("employee_id", e.target.value)} className={filterCls}>
                            <option value="">All employees</option>
                            {employees.map((e) => <option key={e.id} value={e.id}>{e.employee_name}</option>)}
                        </select>
                        <select data-testid="filter-status" value={filters.approval_status} onChange={(e) => setF("approval_status", e.target.value)} className={filterCls}>
                            <option value="">All statuses</option>
                            <option value="pending">Pending</option>
                            <option value="approved">Approved</option>
                            <option value="rejected">Rejected</option>
                        </select>
                        <input data-testid="filter-from" type="date" value={filters.date_from} onChange={(e) => setF("date_from", e.target.value)} className={filterCls} />
                        <span className="text-xs text-[var(--rom-text-3)]">to</span>
                        <input data-testid="filter-to" type="date" value={filters.date_to} onChange={(e) => setF("date_to", e.target.value)} className={filterCls} />
                    </div>
                    {isManage && sel.size > 0 && (
                        <button data-testid="bulk-delete-btn" onClick={bulkDelete} className="btn btn-sm" style={{ background: "#dc2626", color: "white" }}>
                            <Trash size={13} /> Delete {sel.size}
                        </button>
                    )}
                </div>
                <div className="overflow-x-auto">
                    <table className="rom-table">
                        <thead>
                            <tr>
                                {isManage && <th className="w-8"><input type="checkbox" checked={items.length > 0 && sel.size === items.length} onChange={toggleAll} data-testid="select-all" /></th>}
                                <th>Date</th><th>Employee</th><th>Type</th><th>Description</th><th>Project</th>
                                <th className="numeric">Hours</th><th className="numeric">Cost</th><th>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan={isManage ? 9 : 8} className="text-center py-10 text-[var(--rom-text-3)]">Loading…</td></tr>
                            ) : items.length === 0 ? (
                                <tr><td colSpan={isManage ? 9 : 8} className="text-center py-10 text-[var(--rom-text-3)]">No entries match these filters.</td></tr>
                            ) : items.map((e) => {
                                const cfg = STATUS_CFG[e.approval_status] || STATUS_CFG.pending;
                                return (
                                    <tr key={e.id} data-testid={`entry-row-${e.id}`}>
                                        {isManage && <td><input type="checkbox" checked={sel.has(e.id)} onChange={() => toggle(e.id)} data-testid={`select-${e.id}`} /></td>}
                                        <td className="font-mono text-xs whitespace-nowrap">{e.work_date}</td>
                                        <td className="font-semibold">{e.employee_name}</td>
                                        <td><span className={`chip ${e.entry_type === "time_off" ? "chip-orange" : "chip-navy"}`}>{e.entry_type === "time_off" ? "Time off" : "Work"}</span></td>
                                        <td className="text-[var(--rom-text-2)] max-w-[200px] truncate">{e.type_raw || e.time_off_type || "—"}</td>
                                        <td className="text-[var(--rom-text-2)] font-mono text-xs">{e.project_name || e.job_ref || "—"}</td>
                                        <td className="numeric font-semibold">{(e.decimal_hours || 0).toFixed(1)}h{e.overtime_hours > 0 && <span className="text-[var(--rom-orange-dark)] ml-1 text-[10px]">+{e.overtime_hours.toFixed(1)} OT</span>}</td>
                                        <td className="numeric font-mono">{formatCurrency(e.cost_amount)}</td>
                                        <td><span className={`chip ${cfg.chip}`}>{cfg.label}</span></td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

const filterCls = "h-8 px-2 rounded-[4px] border border-[var(--rom-border)] text-xs focus:border-[var(--rom-orange)] focus:outline-none";

function Kpi({ icon: Icon, label, value, accent }) {
    const color = { orange: "var(--rom-orange)", navy: "var(--rom-navy)", green: "var(--rom-green)" }[accent] || "var(--rom-navy)";
    return (
        <div className="rom-card">
            <div className="rom-card-body">
                <div className="flex items-start justify-between">
                    <div className="text-[11px] uppercase tracking-[0.14em] text-[var(--rom-text-2)] font-semibold">{label}</div>
                    <div className="w-8 h-8 rounded-[4px] flex items-center justify-center" style={{ background: `${color}14`, color }}><Icon size={16} weight="bold" /></div>
                </div>
                <div className="mt-3 font-display font-black text-3xl tracking-tight" style={{ color }}>{value}</div>
            </div>
        </div>
    );
}
